import { drawGlassEffect } from '../filters/glass';
import { drawLiquidGlassEffect } from '../filters/liquidGlass';
import { normalizeDimensions } from '../shape';
import { getBackNodesHash } from '../utils/layers';
import { resolveRadius } from '../utils/radius';
import { getCacheByType } from '../utils/raster';

// Cache levels: base, quarter, half to provide smooth transitions at different zoom levels
const CACHE_LEVELS = {
  base: 1,
  quarter: 0.25,
  half: 0.5,
};

// Upper bound on offscreen processing pixels to avoid huge blits/filters
const OFFSCREEN_MAX_PIXELS = 1800 * 1000; // ~1.8MP

// Constants for backdrop filter values
const BACKDROP_FILTER_NONE = 'none';
const BACKDROP_FILTER_BLUR_ZERO_PX = 'blur(0px)';
const BACKDROP_FILTER_BLUR_ZERO = 'blur(0)';
const BACKDROP_FILTER_SATURATE_ZERO = 'saturate(0%)';
const BACKDROP_FILTER_LIQUID_GLASS = 'liquid-glass';
const BACKDROP_FILTER_GLASS = 'glass(';

export function drawBackdropFilter(renderContext, node, callback) {
  if (!node.backdropFilter || node.backdropFilter === BACKDROP_FILTER_NONE) {
    return callback();
  }

  const backdropFilter = node.backdropFilter || '';

  // Early exit if filter values effectively have no visual effect
  if (
    backdropFilter === BACKDROP_FILTER_BLUR_ZERO_PX ||
    backdropFilter === BACKDROP_FILTER_BLUR_ZERO ||
    backdropFilter === BACKDROP_FILTER_SATURATE_ZERO ||
    isBackdropFilterNoop(backdropFilter)
  ) {
    return callback();
  }

  if (backdropFilter.includes(BACKDROP_FILTER_LIQUID_GLASS)) {
    drawLiquidGlassEffect(renderContext, node);
  } else if (backdropFilter.includes(BACKDROP_FILTER_GLASS)) {
    drawGlassEffect(renderContext, node);
  } else {
    drawBackdropFilterImpl(renderContext, node);
  }
  callback();
}

// Determine the appropriate cache level based on zoom
function getCacheLevel(scaleValue) {
  if (scaleValue < 0.5) return CACHE_LEVELS.quarter;
  if (scaleValue < 1) return CACHE_LEVELS.half;
  return CACHE_LEVELS.base;
}

// Generate a stable cache key that considers zoom ranges instead of exact values
function generateCacheKey(backNodesHash, box, scale, filter, panX = 0, panY = 0) {
  // Quantize scale to reduce cache variations during smooth zooming
  const quantizedScale = Math.round(scale * 4) / 4; // 0.25 steps

  // Use 1px quantization for positions to avoid visible misalignment
  const quantizedX = Math.round(box.x);
  const quantizedY = Math.round(box.y);
  const quantizedW = Math.round(box.w);
  const quantizedH = Math.round(box.h);
  const quantizedPanX = Math.round(panX);
  const quantizedPanY = Math.round(panY);

  return `${backNodesHash}|${quantizedX},${quantizedY},${quantizedW},${quantizedH}|${quantizedScale}|P${quantizedPanX},${quantizedPanY}|${filter}`;
}

// Simple memo for scaled filter strings to avoid repeated regex work
const FILTER_MEMO_MAX = 200;
const FILTER_MEMO = new Map();

function drawBackdropFilterImpl(renderContext, node) {
  const { context } = renderContext;
  const { scale: scaleProvider } = renderContext.providers;
  const scaleValue = scaleProvider.scale;
  const backdropCache = getCacheByType(renderContext, 'backdropCache');

  const box = renderContext.api.getNodeBox(node);
  const x = node.w < 0 ? node.x + node.w : node.x;
  const y = node.h < 0 ? node.y + node.h : node.y;
  const w = Math.abs(node.w);
  const h = Math.abs(node.h);

  // Early exit if dimensions are too small to be meaningful
  if (w < 1 || h < 1) {
    return;
  }

  // Save the current state to isolate backdrop filter drawing
  context.save();

  try {
    const dpr = renderContext.dpr;
    const isRenderingToCache = !!renderContext._renderingToCache && !!renderContext.renderConfig;
    // Base level based on zoom
    const baseLevel = getCacheLevel(scaleValue);
    // Dynamic downscale for heavy blur to reduce cost while preserving appearance
    const visualScaleForBlur = isRenderingToCache ? 1 : scaleValue;
    const rawBlur = getBlurRadiusPx(node.backdropFilter);
    const quantForBlur = Math.round(visualScaleForBlur * baseLevel * 4) / 4;
    const scaledBlurPx = rawBlur > 0 ? rawBlur * dpr * quantForBlur : 0;
    const blurDownscale = getBlurDownscaleMultiplier(scaledBlurPx);
    let cacheLevel = baseLevel * blurDownscale;

    // When rendering into a raster cache (frame/group cache), the context has
    // a pure scale transform applied (no translation) via renderConfig.scaleX/Y.
    // In that case, sampling coordinates must be computed in cache space using
    // renderConfig.scaleX/Y, and NOT using stage translate/scale.
    const targetScaleX = isRenderingToCache ? renderContext.renderConfig.scaleX : scaleValue;
    const targetScaleY = isRenderingToCache ? renderContext.renderConfig.scaleY : scaleValue;

    // Area-based cap to limit processing cost on giant nodes
    {
      const pixelEstimate = w * targetScaleX * (h * targetScaleY) * (cacheLevel * cacheLevel);
      if (pixelEstimate > OFFSCREEN_MAX_PIXELS) {
        const factor = Math.sqrt(OFFSCREEN_MAX_PIXELS / pixelEstimate);
        cacheLevel = Math.max(0.2, cacheLevel * factor);
      }
    }

    // Calculate the physical dimensions and position on the current canvas
    let canvasWidth = Math.ceil(w * targetScaleX * cacheLevel);
    let canvasHeight = Math.ceil(h * targetScaleY * cacheLevel);

    // Coordinates for getImageData are in the canvas's pixel space
    let sourceX;
    let sourceY;
    if (isRenderingToCache) {
      const origin = renderContext._rasterOrigin || { x: 0, y: 0 };
      const localX = box.x - origin.x;
      const localY = box.y - origin.y;
      sourceX = Math.floor(localX * targetScaleX * cacheLevel);
      sourceY = Math.floor(localY * targetScaleY * cacheLevel);
    } else {
      sourceX = Math.floor((box.x * targetScaleX + (scaleProvider.translateX || 0)) * cacheLevel);
      sourceY = Math.floor((box.y * targetScaleY + (scaleProvider.translateY || 0)) * cacheLevel);
    }

    // Store original values for calculating adjustments
    const originalSourceX = sourceX;
    const originalSourceY = sourceY;
    const originalCanvasWidth = canvasWidth;
    const originalCanvasHeight = canvasHeight;

    // Get canvas bounds
    const maxCanvasWidth = context.canvas.width * cacheLevel;
    const maxCanvasHeight = context.canvas.height * cacheLevel;

    // Calculate the intersection of the node area with the canvas bounds
    const intersectionLeft = Math.max(0, sourceX);
    const intersectionTop = Math.max(0, sourceY);
    const intersectionRight = Math.min(maxCanvasWidth, sourceX + canvasWidth);
    const intersectionBottom = Math.min(maxCanvasHeight, sourceY + canvasHeight);

    // Calculate the actual area we can capture from the canvas
    const captureWidth = Math.max(0, intersectionRight - intersectionLeft);
    const captureHeight = Math.max(0, intersectionBottom - intersectionTop);

    // Early exit if there's no intersection with the canvas
    if (captureWidth <= 0 || captureHeight <= 0) {
      return;
    }

    // Clamp source rect to the actual intersection to prevent out-of-bounds sampling
    sourceX = intersectionLeft;
    sourceY = intersectionTop;
    // Ensure we never create a zero-sized processing canvas
    canvasWidth = Math.max(1, Math.floor(captureWidth));
    canvasHeight = Math.max(1, Math.floor(captureHeight));

    // Use simplified cache key for better performance
    const backNodesHash = getBackNodesHash(renderContext, node);
    const ctxKind = isRenderingToCache ? 'cache' : 'main';
    const keyScale = isRenderingToCache ? 1 : scaleValue; // cache-space sampling doesn't depend on stage zoom
    const panX = isRenderingToCache ? 0 : scaleProvider.translateX || 0;
    const panY = isRenderingToCache ? 0 : scaleProvider.translateY || 0;
    const quantCacheLevel = Math.round(cacheLevel * 100) / 100;
    const cacheKey =
      `${ctxKind}|L${quantCacheLevel}|` +
      generateCacheKey(backNodesHash, box, keyScale, node.backdropFilter, panX, panY);

    let filteredCanvas;
    const cacheItem = backdropCache.get(node.id);
    const isCacheLevelMatched = cacheItem?.level === cacheLevel;
    const isKeyMatched = cacheItem?.key === cacheKey;
    const isScaling = scaleProvider.isScaling;
    if (cacheItem && (isScaling || (isKeyMatched && isCacheLevelMatched))) {
      filteredCanvas = cacheItem.canvas;
      // Guard against stale/invalid cached canvases
      if (!filteredCanvas || filteredCanvas.width === 0 || filteredCanvas.height === 0) {
        filteredCanvas = null;
        backdropCache.delete(node.id);
      }
    } else {
      // Clean up old cache entry and release pooled canvas if applicable
      if (cacheItem) {
        if (typeof cacheItem.dispose === 'function') {
          cacheItem.dispose();
        } else if (cacheItem.canvas) {
          if (typeof cacheItem.canvas.release === 'function') {
            cacheItem.canvas.release();
          } else {
            cacheItem.canvas.width = 0;
            cacheItem.canvas.height = 0;
          }
        }
      }

      // Only apply filter if the node is at least partially visible on the canvas
      if (
        !(
          sourceX + canvasWidth <= 0 ||
          sourceX >= maxCanvasWidth ||
          sourceY + canvasHeight <= 0 ||
          sourceY >= maxCanvasHeight
        )
      ) {
        // Compute source rect in base canvas space (before cacheLevel scaling)
        const actualSourceX = Math.floor(sourceX / cacheLevel);
        const actualSourceY = Math.floor(sourceY / cacheLevel);
        // Compute source sizes (in base canvas space) and clamp to bounds
        const maxSrcW = Math.max(0, context.canvas.width - actualSourceX);
        const maxSrcH = Math.max(0, context.canvas.height - actualSourceY);
        const actualWidth = Math.max(1, Math.min(Math.floor(canvasWidth / cacheLevel), maxSrcW));
        const actualHeight = Math.max(1, Math.min(Math.floor(canvasHeight / cacheLevel), maxSrcH));

        // Acquire pooled processing canvas
        const poolItem = renderContext.providers?.canvas?.createCanvas(canvasWidth, canvasHeight);
        const offscreenCanvas = poolItem.canvas;
        const offscreenContext = poolItem?.context;

        // Clear the offscreen canvas
        offscreenContext.setTransform(1, 0, 0, 1, 0, 0);
        offscreenContext.clearRect(0, 0, canvasWidth, canvasHeight);

        // Apply the backdrop filter with appropriate scaling
        const visualScale = isRenderingToCache ? 1 : scaleValue;
        const scaledFilter = getScaledFilterMemoized(
          node.backdropFilter,
          dpr,
          visualScale * cacheLevel
        );
        // Early exit if blur radius effectively 0 after scaling
        if (
          scaledFilter === 'none' ||
          scaledFilter.includes('blur(0') ||
          scaledFilter.includes('blur(0.0')
        ) {
          // No-op: keep area as-is without extra draw
        } else {
          offscreenContext.filter = scaledFilter;
        }

        // Draw directly from the current canvas using source rect
        offscreenContext.drawImage(
          context.canvas,
          actualSourceX,
          actualSourceY,
          actualWidth,
          actualHeight,
          0,
          0,
          canvasWidth,
          canvasHeight
        );
        offscreenContext.filter = 'none';

        // Reuse the processing canvas as the filtered result
        filteredCanvas = offscreenCanvas;

        // Cache the filtered result with level information and dispose function
        const cacheResult = {
          key: cacheKey,
          canvas: filteredCanvas,
          level: cacheLevel,
          timestamp: Date.now(),
          dispose: () => {
            if (filteredCanvas && typeof filteredCanvas.release === 'function') {
              filteredCanvas.release();
            }
          },
        };

        backdropCache.set(node.id, cacheResult);
      }
    }

    if (filteredCanvas && filteredCanvas.width > 0 && filteredCanvas.height > 0) {
      // Calculate adjusted logical coordinates and dimensions
      const sourceXOffset = (sourceX - originalSourceX) / cacheLevel;
      const sourceYOffset = (sourceY - originalSourceY) / cacheLevel;
      const widthDiff = (originalCanvasWidth - canvasWidth) / cacheLevel;
      const heightDiff = (originalCanvasHeight - canvasHeight) / cacheLevel;

      const adjustedX =
        x + sourceXOffset / (isRenderingToCache ? renderContext.renderConfig.scaleX : scaleValue);
      const adjustedY =
        y + sourceYOffset / (isRenderingToCache ? renderContext.renderConfig.scaleY : scaleValue);
      const adjustedW =
        w - widthDiff / (isRenderingToCache ? renderContext.renderConfig.scaleX : scaleValue);
      const adjustedH =
        h - heightDiff / (isRenderingToCache ? renderContext.renderConfig.scaleY : scaleValue);

      clipRoundedRect(context, node);

      // Validate destination dimensions to avoid invalid draws
      if (adjustedW > 0 && adjustedH > 0 && isFinite(adjustedW) && isFinite(adjustedH)) {
        // Draw the cached filtered result back onto the main canvas
        context.drawImage(filteredCanvas, adjustedX, adjustedY, adjustedW, adjustedH);
      }

      // Debug: Draw a rect to visualize the backdrop filter area
      if (renderContext.debug) {
        context.strokeStyle = 'rgba(255, 0, 255, 0.8)';
        context.lineWidth = 2;
        context.strokeRect(adjustedX + 1, adjustedY + 1, adjustedW - 2, adjustedH - 2);
      }
    }
  } catch (error) {
    // getImageData can throw security errors for tainted canvases or other issues.
    // Silently fail to avoid breaking the render pipeline
    console.warn(error);
  } finally {
    // Restore context to state before backdrop drawing
    context.restore();
  }
}

function clipRoundedRect(ctx, node) {
  const radius = node.radius;
  if (!radius || radius === 'none') return;
  const { x: normX, y: normY, w: normW, h: normH } = normalizeDimensions(node);
  const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(normW, normH, radius);

  // Path2D caching (best-effort)
  const canUsePath2D = typeof Path2D !== 'undefined';
  if (canUsePath2D) {
    const key = `${node.id}|${normX}|${normY}|${normW}|${normH}|${topLeft}|${topRight}|${bottomRight}|${bottomLeft}`;
    const path = getRoundedRectPathFromCache(key, () => {
      const p = new Path2D();
      p.moveTo(normX + topLeft, normY);
      p.lineTo(normX + normW - topRight, normY);
      p.arcTo(normX + normW, normY, normX + normW, normY + topRight, topRight);
      p.lineTo(normX + normW, normY + normH - bottomRight);
      p.arcTo(
        normX + normW,
        normY + normH,
        normX + normW - bottomRight,
        normY + normH,
        bottomRight
      );
      p.lineTo(normX + bottomLeft, normY + normH);
      p.arcTo(normX, normY + normH, normX, normY + normH - bottomLeft, bottomLeft);
      p.lineTo(normX, normY + topLeft);
      p.arcTo(normX, normY, normX + topLeft, normY, topLeft);
      p.closePath();
      return p;
    });
    ctx.clip(path);
    return;
  }

  ctx.beginPath();
  ctx.moveTo(normX + topLeft, normY);
  ctx.lineTo(normX + normW - topRight, normY);
  ctx.arcTo(normX + normW, normY, normX + normW, normY + topRight, topRight);
  ctx.lineTo(normX + normW, normY + normH - bottomRight);
  ctx.arcTo(normX + normW, normY + normH, normX + normW - bottomRight, normY + normH, bottomRight);
  ctx.lineTo(normX + bottomLeft, normY + normH);
  ctx.arcTo(normX, normY + normH, normX, normY + normH - bottomLeft, bottomLeft);
  ctx.lineTo(normX, normY + topLeft);
  ctx.arcTo(normX, normY, normX + topLeft, normY, topLeft);
  ctx.closePath();
  ctx.clip();
}

function scaleBackdropFilter(filter, dpr, scaleValue) {
  if (!filter) return 'none';

  let scaledFilter = filter;

  // Handle blur filter scaling - similar to filter.js implementation
  if (filter.includes('blur')) {
    scaledFilter = filter.replace(/blur\(([^)]+)\)/g, (match, value) => {
      const numValue = parseFloat(value);
      const unit = value.replace(/[\d.]/g, '');
      // Apply both DPR and scale value to maintain visual consistency at different zoom levels
      const quantizedScale = Math.round(scaleValue * 4) / 4;
      return `blur(${numValue * dpr * quantizedScale}${unit})`;
    });
  }

  return scaledFilter;
}

// Helpers
function isBackdropFilterNoop(filter) {
  if (!filter) return true;
  const f = String(filter).trim();
  if (f === '' || f === 'none') return true;
  // If only a 0 blur is present (with potential spaces), treat as no-op
  const cleaned = f.replace(/blur\(\s*0(?:px)?\s*\)/g, '').trim();
  return cleaned === '';
}

function getBlurRadiusPx(filter) {
  if (!filter || typeof filter !== 'string') return 0;
  const match = filter.match(/blur\(([^)]+)\)/);
  if (!match) return 0;
  const raw = match[1].trim();
  const value = parseFloat(raw);
  if (!isFinite(value) || value <= 0) return 0;
  // Assume px when unit omitted; otherwise only px supported here
  return value;
}

function getBlurDownscaleMultiplier(scaledBlurPx) {
  if (!isFinite(scaledBlurPx) || scaledBlurPx <= 0) return 1;
  if (scaledBlurPx < 8) return 1;
  if (scaledBlurPx < 16) return 0.75;
  if (scaledBlurPx < 32) return 0.5;
  if (scaledBlurPx < 64) return 0.35;
  return 0.25;
}

function getScaledFilterMemoized(filter, dpr, scaleValue) {
  if (!filter) return 'none';
  const quantScale = Math.round(scaleValue * 4) / 4;
  const key = `${filter}|${dpr}|${quantScale}`;
  if (FILTER_MEMO.has(key)) return FILTER_MEMO.get(key);

  // If scaled blur is negligible, short-circuit to none
  const rawBlur = getBlurRadiusPx(filter);
  if (rawBlur > 0) {
    const scaled = rawBlur * dpr * quantScale;
    if (scaled < 0.1) {
      FILTER_MEMO.set(key, 'none');
      trimFilterMemo();
      return 'none';
    }
  }

  const scaledFilter = scaleBackdropFilter(filter, dpr, quantScale);
  FILTER_MEMO.set(key, scaledFilter);
  trimFilterMemo();
  return scaledFilter;
}

function trimFilterMemo() {
  if (FILTER_MEMO.size <= FILTER_MEMO_MAX) return;
  // Drop oldest half
  const drop = Math.ceil(FILTER_MEMO.size / 2);
  let i = 0;
  for (const k of FILTER_MEMO.keys()) {
    FILTER_MEMO.delete(k);
    if (++i >= drop) break;
  }
}

// Rounded rect Path2D cache
const RRECT_MEMO_MAX = 200;
const RRECT_MEMO = new Map();
function getRoundedRectPathFromCache(key, factory) {
  const cached = RRECT_MEMO.get(key);
  if (cached) return cached;
  const path = factory();
  RRECT_MEMO.set(key, path);
  if (RRECT_MEMO.size > RRECT_MEMO_MAX) {
    // simple FIFO trim
    const iter = RRECT_MEMO.keys();
    const first = iter.next();
    if (!first.done) RRECT_MEMO.delete(first.value);
  }
  return path;
}
